import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import Transition from '../../utils/Transition';

function InviteCreateSidebar({
  sidebarOpen,
  setSidebarOpen
}) {
  const [organizations, setOrganizations] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    allowed_role: 'user', // default to user
    allowed_email_domain: '',
    expires_at: '' // optional
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  // Fetch organizations for dropdown
  useEffect(() => {
    if (sidebarOpen) {
      const fetchOrganizations = async () => {
        try {
          const response = await axios.get(
            `${Config.APIBaseURL}/customers/`,
            getAxiosConfig()
          );
          setOrganizations(response.data);
        } catch (error) {
          console.error('Error fetching organizations:', error);
        }
      };
      
      fetchOrganizations();
    }
  }, [sidebarOpen]);

  const getAxiosConfig = () => ({
    headers: {
      'access_token': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer_id) {
      newErrors.customer_id = 'Organization is required';
    }
    
    if (!formData.allowed_role) {
      newErrors.allowed_role = 'Role is required';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create a new object with proper data types
      const requestData = {
        customer_id: parseInt(formData.customer_id), // Convert string to integer
        allowed_role: formData.allowed_role,
        allowed_email_domain: formData.allowed_email_domain || null
      };

      // Only add expires_at if it's not empty
      if (formData.expires_at && formData.expires_at.trim() !== '') {
        requestData.expires_at = formData.expires_at;
      }

      const response = await axios.post(
        `${Config.APIBaseURL}/invites/create`,
        requestData,
        getAxiosConfig()
      );
      
      setInviteLink(response.data.invite_link);
    } catch (error) {
      if (error.response?.status === 403) {
        setErrors({ general: 'Not authorized to create invites' });
      } else if (error.response?.status === 404) {
        setErrors({ general: 'Organization not found' });
      } else {
        setErrors({ general: 'Error creating invite. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition
      show={sidebarOpen}
      tag="div"
      className="fixed inset-0 overflow-hidden z-50"
      enter="transition ease-in-out duration-200"
      enterStart="opacity-0"
      enterEnd="opacity-100"
      leave="transition ease-in-out duration-200"
      leaveStart="opacity-100"
      leaveEnd="opacity-0"
    >
      <div className="absolute inset-0 overflow-hidden">
        <Transition
          show={sidebarOpen}
          tag="div"
          className="absolute inset-0 bg-slate-900 bg-opacity-30"
          enter="transition ease-out duration-200"
          enterStart="opacity-0"
          enterEnd="opacity-100"
          leave="transition ease-out duration-200"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          onClick={() => setSidebarOpen(false)}
        />
        
        <div className="fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
          <Transition
            show={sidebarOpen}
            tag="div"
            className="w-screen max-w-md"
            enter="transition ease-in-out duration-500 transform"
            enterStart="translate-x-full"
            enterEnd="translate-x-0"
            leave="transition ease-in-out duration-500 transform"
            leaveStart="translate-x-0"
            leaveEnd="translate-x-full"
          >
            <div className="h-full flex flex-col bg-white dark:bg-slate-800 shadow-xl overflow-y-scroll">
              <div className="px-4 sm:px-6 py-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    Create Invite
                  </h2>
                  <button
                    className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                      <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                    </svg>
                  </button>
                </div>

                {errors.general && (
                  <div className="mb-4 px-4 py-2 bg-red-100 dark:bg-red-400/10 text-red-600 dark:text-red-400 rounded">
                    {errors.general}
                  </div>
                )}

                {inviteLink ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 dark:bg-green-400/10 rounded">
                      <p className="text-green-600 dark:text-green-400 mb-2">Invite created successfully!</p>
                      <p className="text-sm mb-2">Share this link with the user:</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={inviteLink}
                          className="form-input w-full bg-white dark:bg-slate-800"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(inviteLink);
                            // Optional: Show a copied notification
                          }}
                          className="btn-sm bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setSidebarOpen(false);
                          setInviteLink('');
                          setFormData({
                            customer_id: '',
                            allowed_role: 'user',
                            allowed_email_domain: '',
                            expires_at: ''
                          });
                        }}
                        className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                      {/* Organization */}
                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="customer_id">
                          Organization <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="customer_id"
                          name="customer_id"
                          className={`form-select w-full ${errors.customer_id ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                          value={formData.customer_id}
                          onChange={handleChange}
                        >
                          <option value="">Select an organization</option>
                          {organizations.map(org => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                        {errors.customer_id && (
                          <div className="text-xs mt-1 text-red-500">{errors.customer_id}</div>
                        )}
                      </div>

                      {/* Role */}
                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="allowed_role">
                          Role <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="allowed_role"
                          name="allowed_role"
                          className={`form-select w-full ${errors.allowed_role ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                          value={formData.allowed_role}
                          onChange={handleChange}
                        >
                          <option value="user">User</option>
                          <option value="regulator">Regulator</option>
                        </select>
                        {errors.allowed_role && (
                          <div className="text-xs mt-1 text-red-500">{errors.allowed_role}</div>
                        )}
                      </div>

                      {/* Email Domain Restriction */}
                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="allowed_email_domain">
                          Restrict to Email Domain
                        </label>
                        <input
                          id="allowed_email_domain"
                          name="allowed_email_domain"
                          type="text"
                          placeholder="e.g., company.com"
                          className="form-input w-full border-slate-200 dark:border-slate-700"
                          value={formData.allowed_email_domain}
                          onChange={handleChange}
                        />
                        <div className="text-xs mt-1 text-slate-500">
                          Leave empty to allow any email domain
                        </div>
                      </div>

                      {/* Expiry Date */}
                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="expires_at">
                          Expiry Date
                        </label>
                        <input
                          id="expires_at"
                          name="expires_at"
                          type="datetime-local"
                          className="form-input w-full border-slate-200 dark:border-slate-700"
                          value={formData.expires_at}
                          onChange={handleChange}
                        />
                        <div className="text-xs mt-1 text-slate-500">
                          Leave empty to use default expiry (7 days)
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end mt-6">
                      <button
                        type="button"
                        className="btn-sm border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300"
                        onClick={() => setSidebarOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ml-3"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Creating...' : 'Create Invite'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  );
}

export default InviteCreateSidebar; 